import React, { useRef, useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Box, IconButton, Divider, Menu, Tooltip } from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';

// Icons
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import CodeIcon from '@mui/icons-material/Code';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import DataObjectIcon from '@mui/icons-material/DataObject';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const useStyles = makeStyles(theme => ({
    linkInputContainer: {
        position: 'absolute',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: 8,
        backgroundColor: theme.palette.type === 'dark' ? '#374151' : '#FFFFFF',
        borderRadius: 8,
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        border: `1px solid ${theme.palette.type === 'dark' ? '#4B5563' : '#E5E7EB'}`,
    },
    linkInput: {
        width: 250,
        padding: '8px 12px',
        fontSize: 14,
        border: `1px solid ${theme.palette.type === 'dark' ? '#4B5563' : '#D1D5DB'}`,
        borderRadius: 6,
        outline: 'none',
        backgroundColor: theme.palette.type === 'dark' ? '#1F2937' : '#FFFFFF',
        color: theme.palette.type === 'dark' ? '#E5E7EB' : '#374151',
        '&:focus': {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`
        },
        '&::placeholder': {
            color: theme.palette.type === 'dark' ? '#9CA3AF' : '#9CA3AF'
        }
    },
    linkInputButton: {
        padding: '8px 12px',
        fontSize: 13,
        fontWeight: 500,
        border: 'none',
        borderRadius: 6,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    linkInputSubmit: {
        backgroundColor: theme.palette.type === 'dark' ? '#3B82F6' : '#2563EB',
        color: '#FFFFFF',
        '&:hover': {
            backgroundColor: theme.palette.type === 'dark' ? '#2563EB' : '#1D4ED8',
        }
    },
    linkInputCancel: {
        backgroundColor: theme.palette.type === 'dark' ? '#4B5563' : '#E5E7EB',
        color: theme.palette.type === 'dark' ? '#E5E7EB' : '#374151',
        '&:hover': {
            backgroundColor: theme.palette.type === 'dark' ? '#6B7280' : '#D1D5DB',
        }
    },
    editorContainer: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
        '&:focus-within': {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`
        }
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 4,
        padding: theme.spacing(1, 1.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50]
    },
    toolbarDivider: {
        height: 28,
        margin: theme.spacing(0, 0.75),
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.12)'
    },
    toolbarButton: {
        padding: 8,
        borderRadius: 6,
        color: theme.palette.type === 'dark' ? '#E5E7EB' : theme.palette.text.secondary,
        '&:hover': {
            backgroundColor: theme.palette.action.hover
        },
        '&.active': {
            backgroundColor: theme.palette.primary.main + '20',
            color: theme.palette.primary.main
        },
        '& .MuiSvgIcon-root': {
            fontSize: 18,
            fontWeight: 'bold',
            color: theme.palette.type === 'dark' ? '#E5E7EB' : 'inherit'
        }
    },
    headingSelect: {
        fontSize: '1rem',
        fontWeight: 500,
        padding: theme.spacing(0.5, 1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        backgroundColor: theme.palette.type === 'dark' ? '#374151' : '#FFFFFF',
        color: theme.palette.type === 'dark' ? '#E5E7EB' : theme.palette.text.primary,
        cursor: 'pointer',
        minWidth: 100,
        height: 32,
        '&:hover': {
            backgroundColor: theme.palette.type === 'dark' ? '#4B5563' : theme.palette.action.hover
        },
        '&:focus': {
            outline: 'none',
            borderColor: theme.palette.primary.main
        },
        '& option': {
            backgroundColor: theme.palette.type === 'dark' ? '#374151' : '#FFFFFF',
            color: theme.palette.type === 'dark' ? '#E5E7EB' : theme.palette.text.primary,
            fontSize: '1rem',
            padding: theme.spacing(0.5)
        }
    },
    fontFamilySelect: {
        fontSize: '1rem',
        fontWeight: 500,
        padding: theme.spacing(0.5, 0.75),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        backgroundColor: theme.palette.type === 'dark' ? '#374151' : '#FFFFFF',
        color: theme.palette.type === 'dark' ? '#E5E7EB' : theme.palette.text.primary,
        cursor: 'pointer',
        minWidth: 110,
        height: 32,
        '&:hover': {
            backgroundColor: theme.palette.type === 'dark' ? '#4B5563' : theme.palette.action.hover
        },
        '&:focus': {
            outline: 'none',
            borderColor: theme.palette.primary.main
        },
        '& option': {
            backgroundColor: theme.palette.type === 'dark' ? '#374151' : '#FFFFFF',
            color: theme.palette.type === 'dark' ? '#E5E7EB' : theme.palette.text.primary,
            fontSize: '1rem',
            padding: theme.spacing(0.5)
        }
    },
    fontSizeSelect: {
        fontSize: '1rem',
        fontWeight: 500,
        padding: theme.spacing(0.5, 0.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        backgroundColor: theme.palette.type === 'dark' ? '#374151' : '#FFFFFF',
        color: theme.palette.type === 'dark' ? '#E5E7EB' : theme.palette.text.primary,
        cursor: 'pointer',
        minWidth: 55,
        height: 32,
        '&:hover': {
            backgroundColor: theme.palette.type === 'dark' ? '#4B5563' : theme.palette.action.hover
        },
        '&:focus': {
            outline: 'none',
            borderColor: theme.palette.primary.main
        },
        '& option': {
            backgroundColor: theme.palette.type === 'dark' ? '#374151' : '#FFFFFF',
            color: theme.palette.type === 'dark' ? '#E5E7EB' : theme.palette.text.primary,
            fontSize: '1rem',
            padding: theme.spacing(0.5)
        }
    },
    fontSizeControls: {
        display: 'flex',
        alignItems: 'center',
        gap: 2
    },
    fontSizeButton: {
        padding: 4,
        minWidth: 28,
        height: 28,
        borderRadius: 4,
        color: theme.palette.type === 'dark' ? '#E5E7EB' : theme.palette.text.secondary,
        '&:hover': {
            backgroundColor: theme.palette.action.hover
        },
        '&:disabled': {
            color: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.26)'
        },
        '& .MuiSvgIcon-root': {
            fontSize: 16,
            color: theme.palette.type === 'dark' ? '#E5E7EB' : 'inherit'
        }
    },
    editorContent: {
        minHeight: props => props.minHeight || 200,
        maxHeight: props => props.maxHeight || 'none',
        padding: theme.spacing(2),
        outline: 'none',
        fontSize: '1.25rem',
        lineHeight: 1.8,
        color: theme.palette.type === 'dark' ? '#E5E7EB' : theme.palette.text.primary,
        backgroundColor: theme.palette.type === 'dark' ? '#1F2937' : theme.palette.background.paper,
        caretColor: theme.palette.type === 'dark' ? '#FFFFFF' : theme.palette.text.primary,
        overflowY: 'auto',
        '& p': {
            margin: 0,
            marginBottom: theme.spacing(1)
        },
        '& ul': {
            listStyleType: 'disc',
            paddingLeft: '40px',
            marginLeft: 0,
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(1)
        },
        '& ol': {
            listStyleType: 'decimal',
            paddingLeft: '40px',
            marginLeft: 0,
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(1)
        },
        '& li': {
            display: 'list-item',
            marginBottom: theme.spacing(0.5),
            paddingLeft: theme.spacing(0.5)
        },
        '& ul ul': {
            listStyleType: 'circle'
        },
        '& ul ul ul': {
            listStyleType: 'square'
        },
        '& ol ol': {
            listStyleType: 'lower-alpha'
        },
        '& ol ol ol': {
            listStyleType: 'lower-roman'
        },
        '& blockquote': {
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            paddingLeft: theme.spacing(2),
            marginLeft: 0,
            fontStyle: 'italic',
            color: theme.palette.text.secondary
        },
        '& pre': {
            backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
            padding: theme.spacing(1),
            borderRadius: 4,
            fontFamily: 'monospace',
            overflowX: 'auto'
        },
        '& code': {
            backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
            padding: '2px 6px',
            borderRadius: 4,
            fontFamily: 'monospace',
            fontSize: '0.9em'
        },
        '& a': {
            color: theme.palette.primary.main,
            textDecoration: 'underline'
        },
        '& img': {
            maxWidth: '100%',
            height: 'auto',
            cursor: 'pointer',
            borderRadius: 4,
            '&:hover': {
                outline: `2px solid ${theme.palette.primary.main}`
            }
        },
        '& .image-wrapper': {
            position: 'relative',
            display: 'inline-block'
        },
        '& h1': {
            fontSize: '2em',
            fontWeight: 600,
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(1)
        },
        '& h2': {
            fontSize: '1.5em',
            fontWeight: 600,
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(1)
        },
        '& h3': {
            fontSize: '1.17em',
            fontWeight: 600,
            marginTop: theme.spacing(1.5),
            marginBottom: theme.spacing(1)
        },
        '& h4': {
            fontSize: '1em',
            fontWeight: 600,
            marginTop: theme.spacing(1.5),
            marginBottom: theme.spacing(1)
        },
        '& h5': {
            fontSize: '0.83em',
            fontWeight: 600,
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(0.5)
        },
        '& h6': {
            fontSize: '0.67em',
            fontWeight: 600,
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(0.5)
        },
        '&:empty:before': {
            content: 'attr(data-placeholder)',
            color: theme.palette.text.disabled,
            pointerEvents: 'none',
            position: 'absolute'
        },
        '&:focus:empty:before': {
            content: '""'
        }
    },
    characterCount: {
        padding: theme.spacing(0.5, 1.5),
        borderTop: `1px solid ${theme.palette.divider}`,
        fontSize: '1.2rem',
        color: theme.palette.text.secondary,
        textAlign: 'right',
        backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50]
    },
    characterCountWarning: {
        color: theme.palette.warning.main
    },
    characterCountError: {
        color: theme.palette.error.main
    },
    colorPicker: {
        display: 'grid',
        gridTemplateColumns: 'repeat(10, 1fr)',
        gap: 4,
        padding: theme.spacing(1),
        maxWidth: 250
    },
    colorSwatch: {
        width: 20,
        height: 20,
        borderRadius: 4,
        cursor: 'pointer',
        border: `1px solid ${theme.palette.divider}`,
        '&:hover': {
            transform: 'scale(1.2)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }
    }
}));

// Color palette
const COLORS = [
    '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#efefef', '#f3f3f3', '#ffffff',
    '#980000', '#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#4a86e8', '#0000ff', '#9900ff', '#ff00ff',
    '#e6b8af', '#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e0e3', '#c9daf8', '#cfe2f3', '#d9d2e9', '#ead1dc',
    '#dd7e6b', '#ea9999', '#f9cb9c', '#ffe599', '#b6d7a8', '#a2c4c9', '#a4c2f4', '#9fc5e8', '#b4a7d6', '#d5a6bd'
];

// Heading options
const HEADING_OPTIONS = [
    { label: 'Normal', value: 'p' },
    { label: 'Heading 1', value: 'h1' },
    { label: 'Heading 2', value: 'h2' },
    { label: 'Heading 3', value: 'h3' },
    { label: 'Heading 4', value: 'h4' },
    { label: 'Heading 5', value: 'h5' },
    { label: 'Heading 6', value: 'h6' }
];

// Font family options
const FONT_FAMILY_OPTIONS = [
    { label: 'Default', value: '' },
    { label: 'Arial', value: 'Arial, sans-serif' },
    { label: 'Helvetica', value: 'Helvetica, sans-serif' },
    { label: 'Times New Roman', value: 'Times New Roman, serif' },
    { label: 'Georgia', value: 'Georgia, serif' },
    { label: 'Courier New', value: 'Courier New, monospace' },
    { label: 'Verdana', value: 'Verdana, sans-serif' },
    { label: 'Tahoma', value: 'Tahoma, sans-serif' },
    { label: 'Trebuchet MS', value: 'Trebuchet MS, sans-serif' },
    { label: 'Impact', value: 'Impact, sans-serif' },
    { label: 'Comic Sans MS', value: 'Comic Sans MS, cursive' },
    { label: 'Palatino', value: 'Palatino Linotype, serif' },
    { label: 'Lucida Console', value: 'Lucida Console, monospace' },
    { label: 'Garamond', value: 'Garamond, serif' },
    { label: 'Book Antiqua', value: 'Book Antiqua, serif' }
];

// Font size options
const FONT_SIZE_OPTIONS = [
    { label: '8', value: '1' },
    { label: '10', value: '2' },
    { label: '12', value: '3' },
    { label: '14', value: '4' },
    { label: '18', value: '5' },
    { label: '24', value: '6' },
    { label: '36', value: '7' }
];

// Toolbar button component
const ToolbarButton = ({ icon: Icon, onClick, title, active = false, disabled = false, className, iconColor }) => {
    const classes = useStyles();
    return (
        <Tooltip title={title} arrow>
            <span>
                <IconButton
                    size="small"
                    onClick={onClick}
                    disabled={disabled}
                    className={`${classes.toolbarButton} ${active ? 'active' : ''} ${className || ''}`}
                >
                    <Icon style={iconColor ? { color: iconColor } : {}} />
                </IconButton>
            </span>
        </Tooltip>
    );
};

const RichTextEditor = forwardRef(({
    value = '',
    onChange,
    placeholder = '',
    minHeight = 200,
    maxHeight,
    showCharacterCount = false,
    maxCharacters,
    disabled = false,
    toolbarOptions = 'full',
    onImageUpload,
    showParamButton = false,
    className,
    style
}, ref) => {
    const classes = useStyles({ minHeight, maxHeight });
    const editorRef = useRef(null);
    const fontSizeSelectRef = useRef(null);
    const [activeFormats, setActiveFormats] = useState({});
    const [characterCount, setCharacterCount] = useState(0);
    const [currentFontSize, setCurrentFontSize] = useState('3');
    const [userSelectedFontSize, setUserSelectedFontSize] = useState('3');
    const [linkInputVisible, setLinkInputVisible] = useState(false);
    const [linkInputPosition, setLinkInputPosition] = useState({ top: 0, left: 0 });
    const [linkUrl, setLinkUrl] = useState('');
    const linkInputRef = useRef(null);

    // Color picker menu state
    const [textColorAnchor, setTextColorAnchor] = useState(null);
    const [bgColorAnchor, setBgColorAnchor] = useState(null);

    // Track if we're initializing
    const isInitializing = useRef(false);
    // Store last known selection
    const lastSelection = useRef(null);
    // Track if font size was manually changed
    const fontSizeManuallyChanged = useRef(false);

    // Function to get the highest param number from content (both typed and generated)
    const getHighestParamNumber = useCallback(() => {
        const content = editorRef.current?.innerHTML || '';
        const paramMatches = content.match(/\{\{(\d+)\}\}/g) || [];
        let maxNum = 0;
        paramMatches.forEach(match => {
            const num = parseInt(match.match(/\d+/)[0], 10);
            if (num > maxNum) {
                maxNum = num;
            }
        });
        return maxNum;
    }, []);

    // Auto-scroll to cursor position
    const scrollToCursor = useCallback(() => {
        if (!editorRef.current) return;

        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            const editorRect = editorRef.current.getBoundingClientRect();

            // Check if cursor is below visible area
            if (rect.bottom > editorRect.bottom) {
                editorRef.current.scrollTop += (rect.bottom - editorRect.bottom + 30);
            }
            // Check if cursor is above visible area
            else if (rect.top < editorRect.top && rect.top !== 0) {
                editorRef.current.scrollTop -= (editorRect.top - rect.top + 30);
            }
        }
    }, []);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
        focus: () => editorRef.current?.focus(),
        blur: () => editorRef.current?.blur(),
        getContent: () => editorRef.current?.innerHTML || '',
        setContent: (html) => {
            if (editorRef.current) {
                isInitializing.current = true;
                editorRef.current.innerHTML = html;
                handleInput();
                isInitializing.current = false;
            }
        },
        getText: () => editorRef.current?.innerText || '',
        isEmpty: () => !editorRef.current?.innerText?.trim(),
        insertHTML: (html) => {
            editorRef.current?.focus();
            document.execCommand('insertHTML', false, html);
        },
        clear: () => {
            if (editorRef.current) {
                editorRef.current.innerHTML = '';
                handleInput();
            }
        }
    }));

    // Save selection before any operation
    const saveSelection = useCallback(() => {
        const selection = window.getSelection();
        if (selection.rangeCount > 0 && editorRef.current?.contains(selection.anchorNode)) {
            lastSelection.current = selection.getRangeAt(0).cloneRange();
        }
    }, []);

    // Restore selection
    const restoreSelection = useCallback(() => {
        if (lastSelection.current) {
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(lastSelection.current);
        }
    }, []);

    // Set initial content - only when value changes externally
    useEffect(() => {
        if (editorRef.current && value !== undefined) {
            const currentContent = editorRef.current.innerHTML;
            if (value !== currentContent && !document.activeElement?.contains(editorRef.current)) {
                isInitializing.current = true;
                editorRef.current.innerHTML = value;
                updateCharacterCount();
                isInitializing.current = false;
            }
        }
    }, [value]);

    // Update active formats on selection change (excluding font size)
    const updateActiveFormats = useCallback(() => {
        const formats = {
            bold: document.queryCommandState('bold'),
            italic: document.queryCommandState('italic'),
            underline: document.queryCommandState('underline'),
            strikeThrough: document.queryCommandState('strikeThrough'),
            insertUnorderedList: document.queryCommandState('insertUnorderedList'),
            insertOrderedList: document.queryCommandState('insertOrderedList'),
            justifyLeft: document.queryCommandState('justifyLeft'),
            justifyCenter: document.queryCommandState('justifyCenter'),
            justifyRight: document.queryCommandState('justifyRight')
        };
        setActiveFormats(formats);
        // Do NOT update font size here - it causes the issue
    }, []);

    // Handle selection change
    useEffect(() => {
        const handleSelectionChange = () => {
            if (editorRef.current?.contains(document.activeElement) ||
                editorRef.current?.contains(window.getSelection()?.anchorNode)) {
                updateActiveFormats();
                saveSelection();
            }
        };

        document.addEventListener('selectionchange', handleSelectionChange);
        return () => document.removeEventListener('selectionchange', handleSelectionChange);
    }, [updateActiveFormats, saveSelection]);

    // Update character count
    const updateCharacterCount = useCallback(() => {
        const text = editorRef.current?.innerText || '';
        setCharacterCount(text.length);
    }, []);

    // Handle input
    const handleInput = useCallback(() => {
        updateCharacterCount();

        // Clean up browser-added default styles but preserve user-applied colors
        if (editorRef.current) {
            editorRef.current.querySelectorAll('*').forEach(el => {
                if (el.style) {
                    // Remove black text color (browser default) unless user applied it
                    const color = el.style.color;
                    if (color && !el.hasAttribute('data-user-color')) {
                        if (color === 'rgb(0, 0, 0)' || color === '#000000' || color === 'black') {
                            el.style.removeProperty('color');
                        }
                    }

                    // Remove white background (browser default) unless user applied it
                    const bgColor = el.style.backgroundColor;
                    if (bgColor && !el.hasAttribute('data-user-bg')) {
                        if (bgColor === 'rgb(255, 255, 255)' || bgColor === '#ffffff' || bgColor === 'white') {
                            el.style.removeProperty('background-color');
                            el.style.removeProperty('background');
                        }
                    }
                }
            });
        }

        if (onChange && !isInitializing.current) {
            onChange(editorRef.current?.innerHTML || '');
        }
        // Auto-scroll after input
        setTimeout(scrollToCursor, 0);
    }, [onChange, updateCharacterCount, scrollToCursor]);

    // Execute command
    const execCommand = useCallback((command, value = null) => {
        saveSelection();
        editorRef.current?.focus();
        restoreSelection();
        document.execCommand(command, false, value);
        handleInput();
        updateActiveFormats();
    }, [handleInput, updateActiveFormats, saveSelection, restoreSelection]);

    // Format block (headings)
    const formatBlock = useCallback((tag) => {
        execCommand('formatBlock', tag);
    }, [execCommand]);

    // Set font family
    const setFontFamily = useCallback((fontFamily) => {
        if (fontFamily) {
            execCommand('fontName', fontFamily);
        }
    }, [execCommand]);

    // Set font size
    const setFontSize = useCallback((size) => {
        saveSelection();
        editorRef.current?.focus();
        restoreSelection();
        document.execCommand('fontSize', false, size);
        setCurrentFontSize(size);
        setUserSelectedFontSize(size);
        fontSizeManuallyChanged.current = true;
        handleInput();
    }, [saveSelection, restoreSelection, handleInput]);

    // Increase font size
    const increaseFontSize = useCallback(() => {
        const currentIndex = FONT_SIZE_OPTIONS.findIndex(opt => opt.value === currentFontSize);
        if (currentIndex < FONT_SIZE_OPTIONS.length - 1) {
            const newSize = FONT_SIZE_OPTIONS[currentIndex + 1].value;
            setFontSize(newSize);
        }
    }, [currentFontSize, setFontSize]);

    // Decrease font size
    const decreaseFontSize = useCallback(() => {
        const currentIndex = FONT_SIZE_OPTIONS.findIndex(opt => opt.value === currentFontSize);
        if (currentIndex > 0) {
            const newSize = FONT_SIZE_OPTIONS[currentIndex - 1].value;
            setFontSize(newSize);
        }
    }, [currentFontSize, setFontSize]);

    // Insert link
    const insertLink = useCallback(() => {
        saveSelection();

        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            const editorRect = editorRef.current?.getBoundingClientRect();

            if (editorRect) {
                // Position the input near the cursor
                let top = rect.bottom - editorRect.top + editorRef.current.scrollTop + 5;
                let left = rect.left - editorRect.left;

                // Make sure it doesn't go outside the editor
                if (left + 320 > editorRect.width) {
                    left = editorRect.width - 320;
                }
                if (left < 0) left = 10;

                setLinkInputPosition({ top, left });
                setLinkUrl('https://');
                setLinkInputVisible(true);

                // Focus the input after it renders
                setTimeout(() => {
                    linkInputRef.current?.focus();
                    linkInputRef.current?.select();
                }, 10);
            }
        }
    }, [saveSelection]);

    // Handle link submit
    const handleLinkSubmit = useCallback(() => {
        if (linkUrl && linkUrl !== 'https://') {
            restoreSelection();
            editorRef.current?.focus();

            setTimeout(() => {
                restoreSelection();
                document.execCommand('createLink', false, linkUrl);
                handleInput();
            }, 0);
        }
        setLinkInputVisible(false);
        setLinkUrl('');
    }, [linkUrl, restoreSelection, handleInput]);

    // Handle link cancel
    const handleLinkCancel = useCallback(() => {
        setLinkInputVisible(false);
        setLinkUrl('');
        editorRef.current?.focus();
        restoreSelection();
    }, [restoreSelection]);

    // Handle link input keydown
    const handleLinkInputKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleLinkSubmit();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleLinkCancel();
        }
    }, [handleLinkSubmit, handleLinkCancel]);

    // Insert image
    const insertImage = useCallback(() => {
        const insertImageWithWrapper = (url) => {
            // Create a unique ID for this image
            const imageId = `img-${Date.now()}`;

            // Insert image followed by a new paragraph for typing below
            const imageHtml = `<img id="${imageId}" src="${url}" style="max-width: 100%; height: auto; display: block; margin: 8px 0;" /><p><br></p>`;
            execCommand('insertHTML', imageHtml);

            // Focus on the new paragraph after image insertion
            setTimeout(() => {
                const img = editorRef.current?.querySelector(`#${imageId}`);
                if (img) {
                    const nextSibling = img.nextElementSibling;
                    if (nextSibling) {
                        const range = document.createRange();
                        const selection = window.getSelection();
                        range.setStart(nextSibling, 0);
                        range.collapse(true);
                        selection.removeAllRanges();
                        selection.addRange(range);
                    }
                }
            }, 10);
        };

        if (onImageUpload) {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.onchange = async (e) => {
                const file = e.target.files[0];
                if (file) {
                    const url = await onImageUpload(file);
                    if (url) {
                        insertImageWithWrapper(url);
                    }
                }
            };
            input.click();
        } else {
            const url = prompt('Enter image URL:', 'https://');
            if (url) {
                insertImageWithWrapper(url);
            }
        }
    }, [execCommand, onImageUpload]);

    // Delete image
    const deleteImage = useCallback((img) => {
        if (img && img.parentNode) {
            // Check if image is in a wrapper
            const wrapper = img.closest('.image-wrapper');
            const elementToRemove = wrapper || img;

            // Check if there's a next sibling that's an empty paragraph we added
            const nextSibling = elementToRemove.nextElementSibling;

            // Remove the image (or wrapper)
            elementToRemove.parentNode.removeChild(elementToRemove);

            // If the next sibling is an empty paragraph we added, keep it for typing
            // Otherwise, ensure there's a place to type
            if (!nextSibling || nextSibling.tagName !== 'P') {
                // Add a paragraph if needed
                document.execCommand('insertHTML', false, '<p><br></p>');
            }

            handleInput();
        }
    }, [handleInput]);

    // Handle click on images to show delete option
    useEffect(() => {
        const handleEditorClick = (e) => {
            const target = e.target;

            // Check if clicked on an image
            if (target.tagName === 'IMG' && editorRef.current?.contains(target)) {
                // Remove any existing delete buttons
                const existingButtons = editorRef.current.querySelectorAll('.image-delete-btn');
                existingButtons.forEach(btn => btn.remove());

                // Create delete button
                const deleteBtn = document.createElement('button');
                deleteBtn.className = 'image-delete-btn';
                deleteBtn.innerHTML = '✕ Remove';
                deleteBtn.style.cssText = `
                    position: absolute;
                    top: 8px;
                    right: 8px;
                    background-color: rgba(239, 68, 68, 0.9);
                    color: white;
                    border: none;
                    border-radius: 4px;
                    padding: 6px 12px;
                    font-size: 12px;
                    font-weight: 500;
                    cursor: pointer;
                    z-index: 10;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                `;
                deleteBtn.onmouseenter = () => {
                    deleteBtn.style.backgroundColor = 'rgba(220, 38, 38, 1)';
                };
                deleteBtn.onmouseleave = () => {
                    deleteBtn.style.backgroundColor = 'rgba(239, 68, 68, 0.9)';
                };
                deleteBtn.onclick = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    deleteImage(target);
                    deleteBtn.remove();
                };

                // Wrap image if not already wrapped
                if (!target.parentNode.classList.contains('image-wrapper')) {
                    const wrapper = document.createElement('span');
                    wrapper.className = 'image-wrapper';
                    wrapper.style.cssText = 'position: relative; display: inline-block;';
                    target.parentNode.insertBefore(wrapper, target);
                    wrapper.appendChild(target);
                    wrapper.appendChild(deleteBtn);
                } else {
                    target.parentNode.appendChild(deleteBtn);
                }
            }
        };

        // Handle click outside image to remove delete button
        const handleClickOutside = (e) => {
            if (e.target.tagName !== 'IMG' && !e.target.classList.contains('image-delete-btn')) {
                const existingButtons = editorRef.current?.querySelectorAll('.image-delete-btn');
                existingButtons?.forEach(btn => btn.remove());
            }
        };

        const editor = editorRef.current;
        if (editor) {
            editor.addEventListener('click', handleEditorClick);
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            if (editor) {
                editor.removeEventListener('click', handleEditorClick);
            }
            document.removeEventListener('click', handleClickOutside);
        };
    }, [deleteImage]);

    // Insert code block
    const insertCodeBlock = useCallback(() => {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const selectedText = range.toString();
            if (selectedText) {
                execCommand('insertHTML', `<code>${selectedText}</code>`);
            } else {
                execCommand('insertHTML', '<pre><code>// Code here</code></pre>');
            }
        }
    }, [execCommand]);

    // Insert blockquote
    const insertBlockquote = useCallback(() => {
        execCommand('formatBlock', 'blockquote');
    }, [execCommand]);

    // Set text color - mark as user-applied
    const setTextColor = useCallback((color) => {
        saveSelection();
        editorRef.current?.focus();
        restoreSelection();
        document.execCommand('foreColor', false, color);

        // Mark all elements with color as user-applied
        setTimeout(() => {
            if (editorRef.current) {
                editorRef.current.querySelectorAll('*').forEach(el => {
                    if (el.style && el.style.color) {
                        el.setAttribute('data-user-color', 'true');
                    }
                });
            }
            handleInput();
        }, 0);

        setTextColorAnchor(null);
    }, [saveSelection, restoreSelection, handleInput]);
    // Set background color - mark as user-applied
    const setBackgroundColor = useCallback((color) => {
        saveSelection();
        editorRef.current?.focus();
        restoreSelection();
        document.execCommand('hiliteColor', false, color);

        // Mark all elements with background-color as user-applied
        setTimeout(() => {
            if (editorRef.current) {
                editorRef.current.querySelectorAll('*').forEach(el => {
                    if (el.style && el.style.backgroundColor && el.style.backgroundColor !== 'transparent') {
                        el.setAttribute('data-user-bg', 'true');
                    }
                });
            }
            handleInput();
        }, 0);

        setBgColorAnchor(null);
    }, [saveSelection, restoreSelection, handleInput]);

    // Insert param placeholder
    const insertParam = useCallback(() => {
        const highestNum = getHighestParamNumber();
        const nextNum = highestNum + 1;
        const paramText = `{{${nextNum}}}`;
        execCommand('insertHTML', paramText);
    }, [execCommand, getHighestParamNumber]);

    // Handle paste
    const handlePaste = useCallback((e) => {
        e.preventDefault();
        const html = e.clipboardData.getData('text/html');
        const text = e.clipboardData.getData('text/plain');

        if (html) {
            const cleanHtml = sanitizeHtml(html);
            document.execCommand('insertHTML', false, cleanHtml);
        } else if (text) {
            document.execCommand('insertText', false, text);
        }

        // Clean up any unwanted background colors and text colors added by browser
        setTimeout(() => {
            cleanupBrowserStyles();
        }, 0);

        handleInput();
    }, [handleInput]);

    // Sanitize HTML
    const sanitizeHtml = (html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('script').forEach(el => el.remove());
        doc.querySelectorAll('style').forEach(el => el.remove());
        doc.querySelectorAll('*').forEach(el => {
            // Remove event handlers
            Array.from(el.attributes).forEach(attr => {
                if (attr.name.startsWith('on')) {
                    el.removeAttribute(attr.name);
                }
            });

            // Clean up inline styles but preserve explicit color/background-color from formatting
            if (el.style) {
                const preservedStyles = {};

                // Preserve user-applied colors (from color picker)
                if (el.style.color && el.style.color !== 'rgb(0, 0, 0)' && el.style.color !== '#000000' && el.style.color !== 'black') {
                    preservedStyles.color = el.style.color;
                }
                if (el.style.backgroundColor && el.style.backgroundColor !== 'rgb(255, 255, 255)' && el.style.backgroundColor !== '#ffffff' && el.style.backgroundColor !== 'white' && el.style.backgroundColor !== 'transparent') {
                    preservedStyles.backgroundColor = el.style.backgroundColor;
                }
                // Preserve font-related styles
                if (el.style.fontFamily) preservedStyles.fontFamily = el.style.fontFamily;
                if (el.style.fontSize) preservedStyles.fontSize = el.style.fontSize;
                if (el.style.fontWeight) preservedStyles.fontWeight = el.style.fontWeight;
                if (el.style.fontStyle) preservedStyles.fontStyle = el.style.fontStyle;
                if (el.style.textDecoration) preservedStyles.textDecoration = el.style.textDecoration;
                if (el.style.textAlign) preservedStyles.textAlign = el.style.textAlign;

                // Clear all styles and reapply preserved ones
                el.removeAttribute('style');
                Object.keys(preservedStyles).forEach(prop => {
                    el.style[prop] = preservedStyles[prop];
                });
            }
        });
        return doc.body.innerHTML;
    };

    // Clean up unwanted browser-added styles in editor
    const cleanupBrowserStyles = useCallback(() => {
        if (!editorRef.current) return;

        editorRef.current.querySelectorAll('*').forEach(el => {
            if (el.style) {
                // Remove black text color (browser default)
                if (el.style.color === 'rgb(0, 0, 0)' || el.style.color === '#000000' || el.style.color === 'black') {
                    el.style.removeProperty('color');
                }
                // Remove white background (browser default)
                if (el.style.backgroundColor === 'rgb(255, 255, 255)' || el.style.backgroundColor === '#ffffff' || el.style.backgroundColor === 'white') {
                    el.style.removeProperty('background-color');
                    el.style.removeProperty('background');
                }
            }
        });
    }, []);
    // Handle key commands
    const handleKeyDown = useCallback((e) => {
        // Handle Shift+Enter - insert line break without formatting
        if (e.key === 'Enter' && e.shiftKey) {
            e.preventDefault();

            // Save current scroll position
            const scrollTop = editorRef.current?.scrollTop;

            // Remove all formatting before inserting line break
            document.execCommand('removeFormat');

            // Insert a line break with a zero-width space to reset formatting
            document.execCommand('insertHTML', false, '<br><span style="font-weight:normal;font-style:normal;text-decoration:none;font-size:1.25rem;">\u200B</span>');

            // Restore scroll position and then scroll to cursor
            setTimeout(() => {
                if (editorRef.current && scrollTop !== undefined) {
                    editorRef.current.scrollTop = scrollTop;
                }
                const selection = window.getSelection();
                if (selection.rangeCount > 0) {
                    const range = selection.getRangeAt(0);
                    const container = range.startContainer;
                    if (container.nodeType === Node.TEXT_NODE && container.textContent === '\u200B') {
                        container.textContent = '';
                    }
                }
                scrollToCursor();
            }, 0);

            handleInput();
            return;
        }

        // Handle regular Enter key
        if (e.key === 'Enter' && !e.shiftKey) {
            // Save current scroll position
            const scrollTop = editorRef.current?.scrollTop;

            // Let default behavior happen, then fix scroll
            setTimeout(() => {
                if (editorRef.current && scrollTop !== undefined) {
                    // Restore scroll first
                    editorRef.current.scrollTop = scrollTop;
                }
                // Then scroll to cursor position
                scrollToCursor();
            }, 0);
            return;
        }

        if (e.key === '{') {
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                const textNode = range.startContainer;
                if (textNode.nodeType === Node.TEXT_NODE) {
                    const cursorPos = range.startOffset;
                    const fullText = textNode.textContent;
                    const textBeforeCursor = fullText.substring(0, cursorPos);

                    if (textBeforeCursor.endsWith('{')) {
                        e.preventDefault();
                        const highestNum = getHighestParamNumber();
                        const nextNum = highestNum + 1;
                        const paramText = `{{${nextNum}}}`;

                        // Get the position of the first { we need to replace
                        const firstBracePos = cursorPos - 1;

                        // Check what's before the first brace
                        const charBeforeFirstBrace = firstBracePos > 0 ? fullText[firstBracePos - 1] : '';

                        // Build the replacement text
                        let insertText;

                        // Check if we're at the very start
                        if (firstBracePos === 0) {
                            insertText = paramText;
                        }
                        // If there's a space before, we need to preserve it with &nbsp;
                        else if (charBeforeFirstBrace === ' ') {
                            // Delete both the space and the { then insert &nbsp; + param
                            range.setStart(textNode, firstBracePos - 1);
                            range.setEnd(textNode, cursorPos);
                            range.deleteContents();
                            insertText = `\u00A0${paramText}`;
                        }
                        // If there's already a nbsp or other preserved whitespace
                        else if (charBeforeFirstBrace === '\u00A0') {
                            range.setStart(textNode, firstBracePos);
                            range.setEnd(textNode, cursorPos);
                            range.deleteContents();
                            insertText = paramText;
                        }
                        // If there's no space before (word directly before {)
                        else if (charBeforeFirstBrace && charBeforeFirstBrace !== '\n' && charBeforeFirstBrace !== '\r' && charBeforeFirstBrace !== '\t') {
                            range.setStart(textNode, firstBracePos);
                            range.setEnd(textNode, cursorPos);
                            range.deleteContents();
                            insertText = `\u00A0${paramText}`;
                        }
                        // Default case (newlines, tabs, or other)
                        else {
                            range.setStart(textNode, firstBracePos);
                            range.setEnd(textNode, cursorPos);
                            range.deleteContents();
                            insertText = paramText;
                        }

                        document.execCommand('insertHTML', false, insertText);
                        handleInput();
                        return;
                    }
                }
            }
        }

        if (e.ctrlKey || e.metaKey) {
            switch (e.key.toLowerCase()) {
                case 'b':
                    e.preventDefault();
                    execCommand('bold');
                    break;
                case 'i':
                    e.preventDefault();
                    execCommand('italic');
                    break;
                case 'u':
                    e.preventDefault();
                    execCommand('underline');
                    break;
                case 'z':
                    if (e.shiftKey) {
                        e.preventDefault();
                        execCommand('redo');
                    }
                    break;
                default:
                    break;
            }
        }
    }, [execCommand, getHighestParamNumber, handleInput, scrollToCursor]);
    // Define toolbar groups
    const getToolbarConfig = () => {
        if (Array.isArray(toolbarOptions)) {
            return toolbarOptions;
        }

        switch (toolbarOptions) {
            case 'minimal':
                return ['bold', 'italic', 'underline', '|', 'link'];
            case 'basic':
                return ['undo', 'redo', '|', 'heading', '|', 'bold', 'italic', 'underline', '|', 'bulletList', 'numberedList', '|', 'link'];
            case 'full':
            default:
                return [
                    'undo', 'redo', '|',
                    'heading', '|',
                    'fontFamily', 'fontSize', 'fontSizeControls', '|',
                    'bold', 'italic', 'underline', 'strikethrough', '|',
                    'textColor', 'bgColor', '|',
                    'bulletList', 'numberedList', '|',
                    'alignLeft', 'alignCenter', 'alignRight', '|',
                    'link', 'image', '|',
                    'blockquote', 'code', '|',
                    'clearFormat'
                ];
        }
    };

    const toolbarConfig = getToolbarConfig();

    // Get next param number for tooltip
    const getNextParamNumber = () => {
        return getHighestParamNumber() + 1;
    };

    // Handle font size select change
    const handleFontSizeSelectChange = useCallback((e) => {
        const newSize = e.target.value;
        setFontSize(newSize);
    }, [setFontSize]);

    // Render toolbar item
    const renderToolbarItem = (item, index) => {
        if (item === '|') {
            return <Divider key={index} orientation="vertical" className={classes.toolbarDivider} flexItem />;
        }

        switch (item) {
            case 'undo':
                return <ToolbarButton key={item} icon={UndoIcon} onClick={() => execCommand('undo')} title="Undo (Ctrl+Z)" />;
            case 'redo':
                return <ToolbarButton key={item} icon={RedoIcon} onClick={() => execCommand('redo')} title="Redo (Ctrl+Shift+Z)" />;
            case 'heading':
                return (
                    <Tooltip key={item} title="Heading" arrow>
                        <select
                            className={classes.headingSelect}
                            onChange={(e) => formatBlock(e.target.value)}
                            defaultValue="p"
                        >
                            {HEADING_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </Tooltip>
                );
            case 'fontFamily':
                return (
                    <Tooltip key={item} title="Font Family" arrow>
                        <select
                            className={classes.fontFamilySelect}
                            onChange={(e) => setFontFamily(e.target.value)}
                            defaultValue=""
                        >
                            {FONT_FAMILY_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value} style={{ fontFamily: opt.value || 'inherit' }}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </Tooltip>
                );
            case 'fontSize':
                return (
                    <Tooltip key={item} title="Font Size" arrow>
                        <select
                            ref={fontSizeSelectRef}
                            className={classes.fontSizeSelect}
                            onChange={handleFontSizeSelectChange}
                            value={currentFontSize}
                        >
                            {FONT_SIZE_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </Tooltip>
                );
            case 'fontSizeControls':
                return (
                    <Box key={item} className={classes.fontSizeControls}>
                        <Tooltip title="Decrease Font Size" arrow>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={decreaseFontSize}
                                    className={classes.fontSizeButton}
                                    disabled={currentFontSize === '1'}
                                >
                                    <RemoveIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Increase Font Size" arrow>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={increaseFontSize}
                                    className={classes.fontSizeButton}
                                    disabled={currentFontSize === '7'}
                                >
                                    <AddIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                );
            case 'bold':
                return <ToolbarButton key={item} icon={FormatBoldIcon} onClick={() => execCommand('bold')} title="Bold (Ctrl+B)" active={activeFormats.bold} />;
            case 'italic':
                return <ToolbarButton key={item} icon={FormatItalicIcon} onClick={() => execCommand('italic')} title="Italic (Ctrl+I)" active={activeFormats.italic} />;
            case 'underline':
                return <ToolbarButton key={item} icon={FormatUnderlinedIcon} onClick={() => execCommand('underline')} title="Underline (Ctrl+U)" active={activeFormats.underline} />;
            case 'strikethrough':
                return <ToolbarButton key={item} icon={StrikethroughSIcon} onClick={() => execCommand('strikeThrough')} title="Strikethrough" active={activeFormats.strikeThrough} />;
            case 'bulletList':
                return <ToolbarButton key={item} icon={FormatListBulletedIcon} onClick={() => execCommand('insertUnorderedList')} title="Bullet List" active={activeFormats.insertUnorderedList} />;
            case 'numberedList':
                return <ToolbarButton key={item} icon={FormatListNumberedIcon} onClick={() => execCommand('insertOrderedList')} title="Numbered List" active={activeFormats.insertOrderedList} />;
            case 'alignLeft':
                return <ToolbarButton key={item} icon={FormatAlignLeftIcon} onClick={() => execCommand('justifyLeft')} title="Align Left" active={activeFormats.justifyLeft} />;
            case 'alignCenter':
                return <ToolbarButton key={item} icon={FormatAlignCenterIcon} onClick={() => execCommand('justifyCenter')} title="Align Center" active={activeFormats.justifyCenter} />;
            case 'alignRight':
                return <ToolbarButton key={item} icon={FormatAlignRightIcon} onClick={() => execCommand('justifyRight')} title="Align Right" active={activeFormats.justifyRight} />;
            case 'link':
                return <ToolbarButton key={item} icon={LinkIcon} onClick={insertLink} title="Insert Link" />;
            case 'image':
                return <ToolbarButton key={item} icon={ImageIcon} onClick={insertImage} title="Insert Image" />;
            case 'blockquote':
                return <ToolbarButton key={item} icon={FormatQuoteIcon} onClick={insertBlockquote} title="Blockquote" />;
            case 'code':
                return <ToolbarButton key={item} icon={CodeIcon} onClick={insertCodeBlock} title="Code" />;
            case 'clearFormat':
                return <ToolbarButton key={item} icon={FormatClearIcon} onClick={() => execCommand('removeFormat')} title="Clear Formatting" />;
            case 'param':
                return (
                    <Tooltip key={item} title={`Insert Parameter {{${getNextParamNumber()}}}`} arrow>
                        <button
                            onClick={insertParam}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 4,
                                padding: '6px 12px',
                                fontSize: 13,
                                fontWeight: 500,
                                color: '#374151',
                                backgroundColor: '#E5E7EB',
                                border: 'none',
                                borderRadius: 6,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#D1D5DB';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#E5E7EB';
                            }}
                        >
                            <DataObjectIcon style={{ fontSize: 16, display: 'flex' }} />
                            <span style={{ lineHeight: 1 }}>Insert Param</span>
                        </button>
                    </Tooltip>
                );
            case 'textColor':
                return (
                    <React.Fragment key={item}>
                        <ToolbarButton
                            icon={FormatColorTextIcon}
                            onClick={(e) => setTextColorAnchor(e.currentTarget)}
                            title="Text Color"
                        />
                        <Menu
                            anchorEl={textColorAnchor}
                            open={Boolean(textColorAnchor)}
                            onClose={() => setTextColorAnchor(null)}
                        >
                            <Box className={classes.colorPicker}>
                                {COLORS.map(color => (
                                    <div
                                        key={color}
                                        className={classes.colorSwatch}
                                        style={{ backgroundColor: color }}
                                        onClick={() => setTextColor(color)}
                                    />
                                ))}
                            </Box>
                        </Menu>
                    </React.Fragment>
                );
            case 'bgColor':
                return (
                    <React.Fragment key={item}>
                        <ToolbarButton
                            icon={FormatColorFillIcon}
                            onClick={(e) => setBgColorAnchor(e.currentTarget)}
                            title="Background Color"
                        />
                        <Menu
                            anchorEl={bgColorAnchor}
                            open={Boolean(bgColorAnchor)}
                            onClose={() => setBgColorAnchor(null)}
                        >
                            <Box className={classes.colorPicker}>
                                {COLORS.map(color => (
                                    <div
                                        key={color}
                                        className={classes.colorSwatch}
                                        style={{ backgroundColor: color }}
                                        onClick={() => setBackgroundColor(color)}
                                    />
                                ))}
                            </Box>
                        </Menu>
                    </React.Fragment>
                );
            default:
                return null;
        }
    };

    // Get character count class
    const getCharacterCountClass = () => {
        if (!maxCharacters) return '';
        const percentage = (characterCount / maxCharacters) * 100;
        if (percentage >= 100) return classes.characterCountError;
        if (percentage >= 90) return classes.characterCountWarning;
        return '';
    };

    return (
        <Box className={`${classes.editorContainer} ${className || ''}`} style={{ ...style, position: 'relative' }}>
            {/* Toolbar */}
            <Box className={classes.toolbar}>
                {toolbarConfig.map((item, index) => renderToolbarItem(item, index))}
                {showParamButton && !toolbarConfig.includes('param') && (
                    <>
                        <Divider orientation="vertical" className={classes.toolbarDivider} flexItem />
                        {renderToolbarItem('param', 'param-extra')}
                    </>
                )}
            </Box>

            {/* Editor Content */}
            <Box
                ref={editorRef}
                className={classes.editorContent}
                contentEditable={!disabled}
                suppressContentEditableWarning
                onInput={handleInput}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                data-placeholder={placeholder}
                role="textbox"
                aria-multiline="true"
                aria-label="Rich text editor"
            />{/* Link Input Popup */}
            {linkInputVisible && (
                <div
                    className={classes.linkInputContainer}
                    style={{
                        top: linkInputPosition.top,
                        left: linkInputPosition.left
                    }}
                >
                    <input
                        ref={linkInputRef}
                        type="text"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        onKeyDown={handleLinkInputKeyDown}
                        placeholder="Enter URL..."
                        className={classes.linkInput}
                    />
                    <button
                        onClick={handleLinkSubmit}
                        className={`${classes.linkInputButton} ${classes.linkInputSubmit}`}
                    >
                        Add
                    </button>
                    <button
                        onClick={handleLinkCancel}
                        className={`${classes.linkInputButton} ${classes.linkInputCancel}`}
                    >
                        Cancel
                    </button>
                </div>
            )}

            {/* Character Count */}
            {showCharacterCount && (
                <Box className={`${classes.characterCount} ${getCharacterCountClass()}`}>
                    {maxCharacters ? `${characterCount} / ${maxCharacters}` : characterCount} characters
                </Box>
            )}
        </Box>
    );
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;